import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faCirclePlay,
  faCheck,
  faLock,
  faPlay,
  faBookOpen,
  faStar,
  faBullseye,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';

import contiMascot from '../../../imagesHome/conti.png';

import TopBar
  from '../../functionalpagelogin/topBar/TopBar.jsx';

import ContaAplicataSyllabus
  from './components/ContaAplicataSyllabus.jsx';

import {
  useUnitCourseProgress,
  CONTA_APLICATA_COURSE_ID,
} from '../shared/unitCourseProgress';

import './ContaAplicataCourse.css';


/* =====================================================
   COMPONENTA
===================================================== */

const ContaAplicataCourse = () => {
  const navigate = useNavigate();


  /* =====================================================
     DATE CURS DIN BACKEND
  ===================================================== */

  const [
    courseData,
    setCourseData,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState(null);


  /* =====================================================
     UI STATE
  ===================================================== */

  const [
    openUnits,
    setOpenUnits,
  ] = useState([1]);

  const [
    selectedUnitId,
    setSelectedUnitId,
  ] = useState(null);


/* =====================================================
   INCARCARE CURS DIN BAZA DE DATE
===================================================== */

useEffect(() => {
  const loadCourse = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/contaaplicata`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(
          'Cursul de contabilitate aplicata nu a putut fi incarcat.'
        );
      }

      const data = await response.json();

      console.log(
        'Contabilitate aplicata din DB:',
        data
      );

      setCourseData(data);

    } catch (err) {
      console.error(
        'Eroare la incarcarea cursului de contabilitate aplicata:',
        err
      );

      setError(
        err.message
      );

    } finally {
      setLoading(false);
    }
  };

  loadCourse();
}, []);

  /* =====================================================
     CAPITOLE / UNITATI DIN DATABASE
  ===================================================== */

  const courseUnits =
    useMemo(() => {
      return (
        courseData?.chapters ||
        []
      );
    }, [courseData]);


  /* =====================================================
     PROGRES DIN BACKEND
  ===================================================== */

  const {
    completedKeys,
    currentLesson: currentPosition,
  } = useUnitCourseProgress(
    CONTA_APLICATA_COURSE_ID,
    courseUnits
  );


  /* =====================================================
     HELPERS PROGRES
  ===================================================== */

  const getLessonKey = (
    unitId,
    lessonId
  ) => {
    return `${unitId}-${lessonId}`;
  };


  const isLessonCompleted = (
    unitId,
    lessonId
  ) => {
    return (
      completedKeys.has(
          getLessonKey(
            unitId,
            lessonId
          )
        )
    );
  };


  const isCurrentLesson = (
    unitId,
    lessonId
  ) => {
    return (
      currentPosition?.unitId ===
        unitId &&
      currentPosition?.lessonId ===
        lessonId
    );
  };


  /* =====================================================
     UNITATE FINALIZATA
  ===================================================== */

  const isUnitCompleted = (
    unit
  ) => {
    const lessons =
      unit?.lessons || [];

    if (
      lessons.length === 0
    ) {
      return false;
    }

    return lessons.every(
      (lesson) =>
        isLessonCompleted(
          unit.id,
          lesson.id
        )
    );
  };


  /* =====================================================
     UNITATE BLOCATA
  ===================================================== */

  const isUnitLocked = () => {
    return false;
  };


  /* =====================================================
     LECTIE BLOCATA
  ===================================================== */

  const isLessonLocked = () => {
    return false;
  };


  /* =====================================================
     PROGRES UNITATE
  ===================================================== */

  const getUnitProgress = (
    unit
  ) => {
    const lessons =
      unit?.lessons || [];

    const total =
      lessons.length;

    if (
      total === 0
    ) {
      return 0;
    }

    const completed =
      lessons.filter(
        (lesson) =>
          isLessonCompleted(
            unit.id,
            lesson.id
          )
      ).length;

    return Math.round(
      (
        completed /
        total
      ) *
        100
    );
  };


  /* =====================================================
     PROGRES GENERAL
  ===================================================== */

  const totalLessons =
    useMemo(() => {
      return courseUnits.reduce(
        (
          total,
          unit
        ) => {
          return (
            total +
            (
              unit?.lessons
                ?.length ||
              0
            )
          );
        },
        0
      );
    }, [courseUnits]);


  const completedLessons =
    useMemo(() => {
      return courseUnits.reduce(
        (
          total,
          unit
        ) => {
          const lessons =
            unit?.lessons || [];

          const completed =
            lessons.filter(
              (lesson) =>
                completedKeys.has(
                    `${unit.id}-${lesson.id}`
                  )
            ).length;

          return (
            total +
            completed
          );
        },
        0
      );
    }, [courseUnits, completedKeys]);


  const overallProgress =
    totalLessons > 0
      ? Math.round(
          (
            completedLessons /
            totalLessons
          ) *
            100
        )
      : 0;


  /* =====================================================
     DESCHIDERE / INCHIDERE UNITATE
  ===================================================== */

  const toggleUnit = (
    unitId
  ) => {
    setOpenUnits(
      (prev) => {
        if (
          prev.includes(
            unitId
          )
        ) {
          return [];
        }

        return [
          unitId,
        ];
      }
    );
  };


  /* =====================================================
     CLICK LECTIE
  ===================================================== */

  const handleLessonClick = (
    unit,
    lesson,
    locked
  ) => {
    if (locked) {
      return;
    }

    navigate(
      '/contabilitate-aplicata/lesson',
      {
        state: {
          unitId:
            unit.id,

          lessonId:
            lesson.id,

          lessonType:
            lesson.type,

          unitTitle:
            unit.title,

          lessonTitle:
            lesson.title,
        },
      }
    );
  };


  /* =====================================================
     CONTINUA CURSUL
  ===================================================== */

  const handleContinue =
    () => {
      const currentUnit =
        courseUnits.find(
          (unit) =>
            unit.id ===
            currentPosition?.unitId
        );

      const currentLesson =
        currentUnit
          ?.lessons
          ?.find(
            (lesson) =>
              lesson.id ===
              currentPosition?.lessonId
          );

      if (
        !currentUnit ||
        !currentLesson
      ) {
        return;
      }

      handleLessonClick(
        currentUnit,
        currentLesson,
        false
      );
    };


  /* =====================================================
     ICON TIP LECTIE
  ===================================================== */

  const getLessonIcon = (
    type
  ) => {
    switch (type) {
      case 'practice':
        return faStar;

      case 'mission':
        return faBullseye;

      case 'project':
        return faTrophy;

      default:
        return faBookOpen;
    }
  };


  /* =====================================================
     LABEL TIP LECTIE
  ===================================================== */

  const getLessonTypeLabel = (
    type
  ) => {
    switch (type) {
      case 'practice':
        return 'Aplicatie';

      case 'mission':
        return 'Misiune';

      case 'project':
        return 'Proiect final';

      default:
        return 'Lectie';
    }
  };


  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {
    return (
      <div className="conta-aplicata-course-page">

        <div className="conta-aplicata-stats-design-top">
          <TopBar />
        </div>

        <div className="conta-aplicata-course-loading">
          Se incarca cursul...
        </div>

      </div>
    );
  }


  /* =====================================================
     ERROR
  ===================================================== */

  if (error) {
    return (
      <div className="conta-aplicata-course-page">

        <div className="conta-aplicata-stats-design-top">
          <TopBar />
        </div>

        <div className="conta-aplicata-course-loading">
          {error}
        </div>

      </div>
    );
  }


  /* =====================================================
     PROTECTIE DATE
  ===================================================== */

  if (!courseData) {
    return null;
  }


  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className="conta-aplicata-course-page">

      <div className="conta-aplicata-stats-design-top">
        <TopBar />
      </div>


      {selectedUnitId ? (

        <ContaAplicataSyllabus
          unit={
            courseUnits.find(
              (item) =>
                item.id ===
                selectedUnitId
            )
          }
          onBack={() =>
            setSelectedUnitId(
              null
            )
          }
        />

      ) : (

        <div className="conta-aplicata-course-shell">


          {/* =============================================
              HERO
          ============================================= */}

          <section className="conta-aplicata-course-hero">

            <div className="conta-aplicata-course-hero-content">

              <h1>
                {courseData.title}
              </h1>


              <p className="conta-aplicata-course-description">
                {
                  courseData.description
                }
              </p>


              <div className="conta-aplicata-course-progress-block">

                <div className="conta-aplicata-course-progress-top">

                  <span>
                    Progres curs
                  </span>

                  <strong>
                    {
                      overallProgress
                    }
                    %
                  </strong>

                </div>


                <div className="conta-aplicata-course-progress-bar">

                  <div
                    className="conta-aplicata-course-progress-fill"
                    style={{
                      width:
                        `${overallProgress}%`,
                    }}
                  />

                </div>


                <div className="conta-aplicata-course-progress-meta">

                  {
                    completedLessons
                  }

                  {' '}

                  din

                  {' '}

                  {
                    totalLessons
                  }

                  {' '}

                  lectii finalizate

                </div>

              </div>


              <div
                className="conta-aplicata-course-continue"
                onClick={
                  handleContinue
                }
              >

                <p>
                  Continua cursul
                </p>

                <FontAwesomeIcon
                  icon={faPlay}
                />

              </div>

            </div>


            <div className="conta-aplicata-course-mascot-wrapper">

              <img
                src={contiMascot}
                alt="Conti"
                className="conta-aplicata-course-mascot"
              />

            </div>

          </section>


          {/* =============================================
              HEADER CAPITOLE
          ============================================= */}

          <div className="conta-aplicata-course-section-heading">

            <div>

              <h2>
                Unitati de invatare
              </h2>

            </div>


            <p>
              Parcurge lectiile in ordine pentru a debloca urmatoarele unitati.
            </p>

          </div>


          {/* =============================================
              CAPITOLE DIN BAZA DE DATE
          ============================================= */}

          <section className="conta-aplicata-course-units">

            {courseUnits.map(
              (
                unit,
                unitIndex
              ) => {

                const isOpen =
                  openUnits.includes(
                    unit.id
                  );

                const unitLocked =
                  isUnitLocked(
                    unitIndex
                  );

                const unitProgress =
                  getUnitProgress(
                    unit
                  );

                const unitCompleted =
                  isUnitCompleted(
                    unit
                  );

                const lessons =
                  unit?.lessons ||
                  [];


                return (
                  <article
                    key={
                      unit.id
                    }
                    className={[
                      'conta-aplicata-unit-card',

                      unitLocked
                        ? 'is-locked'
                        : '',

                      unitCompleted
                        ? 'is-completed'
                        : '',

                      isOpen
                        ? 'is-open'
                        : '',
                    ]
                      .filter(
                        Boolean
                      )
                      .join(
                        ' '
                      )}
                  >


                    {/* ===================================
                        HEADER CAPITOL
                    =================================== */}

                    <button
                      type="button"
                      className="conta-aplicata-unit-header"
                      onClick={() =>
                        toggleUnit(
                          unit.id
                        )
                      }
                    >

                      <div className="conta-aplicata-unit-number">

                        {unitLocked ? (

                          <FontAwesomeIcon
                            icon={
                              faLock
                            }
                          />

                        ) : unitCompleted ? (

                          <FontAwesomeIcon
                            icon={
                              faCheck
                            }
                          />

                        ) : (

                          unitIndex +
                          1

                        )}

                      </div>


                      <div className="conta-aplicata-unit-header-content">

                        <h3>
                          {
                            unit.title
                          }
                        </h3>


                        <p>
                          {
                            unit.description
                          }
                        </p>

                      </div>


                      <div className="conta-aplicata-unit-header-right">

                        <div className="conta-aplicata-unit-progress">

                          <strong>
                            {
                              unitProgress
                            }
                            %
                          </strong>


                          <div className="conta-aplicata-unit-progress-bar">

                            <div
                              className="conta-aplicata-unit-progress-fill"
                              style={{
                                width:
                                  `${unitProgress}%`,
                              }}
                            />

                          </div>

                        </div>


                        <FontAwesomeIcon
                          icon={
                            isOpen
                              ? faChevronUp
                              : faChevronDown
                          }
                          className="conta-aplicata-unit-chevron"
                        />

                      </div>

                    </button>


                    {/* ===================================
                        LECTII DIN CAPITOL
                    =================================== */}

                    {isOpen && (

                      <div className="conta-aplicata-unit-content">

                        {lessons.map(
                          (
                            lesson,
                            lessonIndex
                          ) => {

                            const completed =
                              isLessonCompleted(
                                unit.id,
                                lesson.id
                              );

                            const current =
                              isCurrentLesson(
                                unit.id,
                                lesson.id
                              );

                            const locked =
                              isLessonLocked(
                                unit,
                                unitIndex,
                                lessonIndex
                              );


                            return (
                              <button
                                key={
                                  lesson.id
                                }
                                type="button"
                                className={[
                                  'conta-aplicata-lesson-row',

                                  completed
                                    ? 'is-completed'
                                    : '',

                                  current
                                    ? 'is-current'
                                    : '',

                                  locked
                                    ? 'is-locked'
                                    : '',

                                  lesson.type
                                    ? `is-${lesson.type}`
                                    : '',
                                ]
                                  .filter(
                                    Boolean
                                  )
                                  .join(
                                    ' '
                                  )}
                                onClick={() =>
                                  handleLessonClick(
                                    unit,
                                    lesson,
                                    locked
                                  )
                                }
                                disabled={
                                  locked
                                }
                              >


                                {/* STATUS */}

                                <div className="conta-aplicata-lesson-status">

                                  {completed ? (

                                    <FontAwesomeIcon
                                      icon={
                                        faCheck
                                      }
                                    />

                                  ) : locked ? (

                                    <FontAwesomeIcon
                                      icon={
                                        faLock
                                      }
                                    />

                                  ) : current ? (

                                    <FontAwesomeIcon
                                      icon={
                                        faPlay
                                      }
                                    />

                                  ) : (

                                    lessonIndex +
                                    1

                                  )}

                                </div>


                                {/* CONTINUT */}

                                <div className="conta-aplicata-lesson-main">

                                  <div className="conta-aplicata-lesson-type">

                                    <FontAwesomeIcon
                                      icon={
                                        getLessonIcon(
                                          lesson.type
                                        )
                                      }
                                    />

                                    {
                                      getLessonTypeLabel(
                                        lesson.type
                                      )
                                    }

                                  </div>


                                  <strong>
                                    {
                                      lesson.title
                                    }
                                  </strong>

                                </div>


                                {/* ACTIUNE */}

                                <div
                                  className={[
                                    'conta-aplicata-lesson-action',

                                    completed
                                      ? 'is-finished'
                                      : locked
                                      ? 'is-locked-action'
                                      : current
                                      ? 'is-continue'
                                      : 'is-start',
                                  ].join(
                                    ' '
                                  )}
                                >

                                  <FontAwesomeIcon
                                    icon={
                                      completed
                                        ? faCircleCheck
                                        : locked
                                        ? faLock
                                        : current
                                        ? faCirclePlay
                                        : faPlay
                                    }
                                  />

                                </div>

                              </button>
                            );
                          }
                        )}


                        {/* =================================
                            FOOTER CAPITOL
                        ================================= */}

                        <div className="conta-aplicata-unit-footer">

                          <button
                            type="button"
                            className="conta-aplicata-unit-footer-secondary"
                            onClick={(
                              event
                            ) => {
                              event.stopPropagation();

                              setSelectedUnitId(
                                unit.id
                              );
                            }}
                          >
                            Vezi programa
                          </button>


                          <button
                            type="button"
                            className="conta-aplicata-unit-footer-primary"
                            onClick={(
                              event
                            ) => {
                              event.stopPropagation();

                              const nextLesson =
                                lessons.find(
                                  (
                                    lesson
                                  ) =>
                                    !isLessonCompleted(
                                      unit.id,
                                      lesson.id
                                    )
                                );

                              if (
                                nextLesson
                              ) {
                                handleLessonClick(
                                  unit,
                                  nextLesson,
                                  false
                                );
                              }
                            }}
                          >

                            {
                              unitCompleted
                                ? 'Recapituleaza'
                                : 'Continua'
                            }

                          </button>

                        </div>

                      </div>
                    )}

                  </article>
                );
              }
            )}

          </section>

        </div>
      )}

    </div>
  );
};


export default ContaAplicataCourse;